/**
 * After the last audible frame, this much quiet means the assistant stopped
 * talking, not just paused between words.
 */
const QUIET_SECONDS = 0.5;
/**
 * Silence this long between the assistant's words, on the session timeline,
 * starts a new stretch of speech: after the caller cut in, its reply. Words
 * run into each other, so a short pause already means it stopped.
 */
const NEW_RUN_SILENCE_MS = 300;
/**
 * The same, measured start to start, for fragments that do not say where
 * they end: generous, as it includes the words themselves.
 */
const NEW_RUN_GAP_MS = 800;
/** Stretches kept; only the recent ones can be a reply. */
const MAX_RUNS = 20;

export interface CrossTalkDeps {
  /** The playback clock, in seconds. */
  audioNow: () => number;
  schedule: (callback: () => void, delayMs: number) => void;
}

/** Settles an overlap; `replyAt` is where the assistant's reply starts. */
export type SettleCrossTalk = (replyAt: number | null) => void;

/**
 * The caller talking over the assistant, and when that is over.
 *
 * The call is full duplex, so the caller can talk over the assistant. Words
 * said over it must not cut its turn into pieces, one per word: the exchange
 * is settled once, when the assistant has gone quiet, or as soon as it starts
 * replying to the caller (a new stretch of speech after they cut in), so its
 * reply is never taken for the rest of the answer it was interrupted in.
 */
export class CrossTalk {
  /** When the last audible frame stops playing, on the playback clock. */
  private audibleUntil = Number.NEGATIVE_INFINITY;
  private waiting: SettleCrossTalk | null = null;
  /** Where the caller cut in, on the session timeline. */
  private cutInMs = 0;
  /** Where the last fragment ends on the timeline, and whether it said so. */
  private lastText: { endMs: number; exact: boolean } | null = null;
  /** Where each stretch of the assistant's speech starts, on the timeline. */
  private runStarts: number[] = [];

  constructor(private readonly deps: CrossTalkDeps) {}

  /** An audible frame of the assistant's voice plays until `endsAt`. */
  noteAudible(endsAt: number): void {
    this.audibleUntil = Math.max(this.audibleUntil, endsAt);
  }

  get talking(): boolean {
    return this.deps.audioNow() < this.audibleUntil + QUIET_SECONDS;
  }

  /**
   * The assistant's transcript from `startMs` (to `endMs`, when known).
   * @returns whether it starts the reply to a caller who cut in: the overlap
   *   must be settled now, before these words join the earlier answer.
   */
  noteText(startMs: number, endMs?: number): boolean {
    const last = this.lastText;
    const newRun =
      last === null ||
      startMs - last.endMs >=
        (last.exact ? NEW_RUN_SILENCE_MS : NEW_RUN_GAP_MS);
    const end = endMs ?? startMs;
    if (last === null || end >= last.endMs) {
      this.lastText = { endMs: end, exact: endMs !== undefined };
    }
    if (!newRun) return false;
    this.runStarts = [...this.runStarts, startMs].slice(-MAX_RUNS);
    return this.waiting !== null && startMs >= this.cutInMs;
  }

  /**
   * Settle once the assistant has gone quiet. While one is already waiting,
   * later calls are ignored: one settle per stretch of overlap.
   * @param cutInMs where the caller's overlapping words start, on the timeline
   */
  whenQuiet(cutInMs: number, settle: SettleCrossTalk): void {
    if (this.waiting) return;
    this.waiting = settle;
    this.cutInMs = cutInMs;
    this.check();
  }

  /**
   * Stop waiting, because the exchange is being settled now.
   * @returns whether anything was waiting.
   */
  cancel(): boolean {
    const was = this.waiting !== null;
    this.waiting = null;
    return was;
  }

  /** Forget the voice and anything waiting on it, e.g. on hang-up. */
  reset(): void {
    this.audibleUntil = Number.NEGATIVE_INFINITY;
    this.waiting = null;
    this.lastText = null;
    this.runStarts = [];
  }

  /** Where the assistant's reply to the caller who cut in starts, if it has. */
  private replyStart(): number | null {
    return this.runStarts.find((start) => start >= this.cutInMs) ?? null;
  }

  private check = (): void => {
    const settle = this.waiting;
    if (!settle) return;
    const quietInSeconds =
      this.audibleUntil + QUIET_SECONDS - this.deps.audioNow();
    if (quietInSeconds > 0) {
      this.deps.schedule(this.check, quietInSeconds * 1000);
      return;
    }
    this.waiting = null;
    settle(this.replyStart());
  };
}
